/* Source: https://github.com/KyberNetwork/kyberswap-interface/blob/860030939a688248772755ad15c2392f1d563e7a/src/components/TradingViewChart/datafeed.tsx */

import { useRef } from 'react';

import { configurationData } from '@/config/chart';
import { constructSymbolInfo, convertResolutionToSeconds, fetchHistory } from '@/helpers/datafeedHelper';
import { RootState, store } from '@/state/store';
import {
    ErrorCallback,
    HistoryCallback,
    IDatafeedChartApi,
    IExternalDatafeed,
    LibrarySymbolInfo,
    OnReadyCallback,
    PeriodParams,
    ResolutionString,
    ResolveCallback,
    SearchSymbolsCallback,
    SubscribeBarsCallback
} from '@/types/charting_library';
import { getUniversalInstrument } from './useInstrument';

export const useDatafeed = (id, chartId, chartInstrument) => {
    const intervalRef = useRef<any>(null);
    // const NDConnection = useMemo(() => new global.NetDania.JsApi.JSONConnection(connectionConfig), []);
    // useEffect(() => {
    //     if (NDConnection && NDConnection?.listeners?.OnHistoricalData?.length >= 5) {
    //         NDConnection.removeListener(global.NetDania.JsApi.Events.ONHISTORICALDATA);
    //     }
    // }, [NDConnection.listeners.OnHistoricalData]);

    return {
        onReady: (callback: OnReadyCallback) => {
            setTimeout(() => callback(configurationData));
        },
        resolveSymbol: async (
            symbolName: string,
            onSymbolResolvedCallback: ResolveCallback,
            onResolveErrorCallback: ErrorCallback
        ) => {
            setTimeout(() => {
                const state: RootState = store.getState();
                const pairs = state.marketPair.pairs;

                const TVWidget = window[chartId]?.TVWidget;

                if (TVWidget) {
                    TVWidget.chart()
                        .onIntervalChanged()
                        .subscribe(null, (resolution: ResolutionString) => {
                            // if (resolution === '1T') TVWidget.chart().setChartType(2);
                            // else TVWidget.chart().setChartType(1);
                            const chartState = localStorage.getItem(`chartState-${id}`);
                            const savedChart = chartState ? JSON.parse(chartState) : undefined;
                            if (savedChart) {
                                savedChart.charts[0].panes[0].sources[0].state.interval = resolution;
                                localStorage.setItem(`chartState-${id}`, JSON.stringify(savedChart));
                            }
                        });

                    TVWidget.subscribe('onAutoSaveNeeded', () => {
                        TVWidget.save((state: any) => {
                            localStorage.setItem(`chartState-${id}`, JSON.stringify(state));
                        });
                    });
                }

                const pairToUse = symbolName;
                // const pairToUse = chartInstrument?.netdania !== symbolName ? chartInstrument?.netdania : symbolName;
                let symbolItem;
                if (pairs.length > 0) {
                    const allSymbols = pairs.map((pair) => {
                        const instrument = getUniversalInstrument(pair.celer);
                        return {
                            symbol: pair.netdania,
                            full_name: instrument?.histPriceSymbol || ':',
                            description: pair.show,
                            exchange: '',
                            type: instrument?.type || '',
                            instrument
                        };
                    });
                    symbolItem = allSymbols.find(({ symbol }) => symbol === pairToUse);
                } else {
                    const instrument = getUniversalInstrument('BTC/USD');
                    symbolItem = {
                        symbol: 'BTC/USD',
                        full_name: instrument?.histPriceSymbol || ':',
                        description: 'BTC/USD',
                        exchange: '',
                        type: instrument?.type || '',
                        instrument
                    };
                }
                if (symbolItem) {
                    const symbolInfo = constructSymbolInfo(symbolItem, configurationData) as any;
                    onSymbolResolvedCallback(symbolInfo);
                } else {
                    onResolveErrorCallback('Cannot resolve symbol');
                }
            }, 0);
        },
        getBars: async (
            symbolInfo: LibrarySymbolInfo,
            resolution: ResolutionString,
            periodParams: PeriodParams,
            onHistory: HistoryCallback,
            onError: ErrorCallback
        ) => {
            if (!window[chartId]?.chartLoaded) {
                onHistory([], { noData: true });
                return;
            }

            const intervalSec = convertResolutionToSeconds(resolution);
            let { from, to } = periodParams;
            const firstDataRequest = periodParams.firstDataRequest;

            // ---- 1️⃣ Clamp initial load aggressively
            if (firstDataRequest) {
                const INITIAL_VISIBLE_BARS = 350;
                from = Math.max(from, to - INITIAL_VISIBLE_BARS * intervalSec);
            }

            // ---- 2️⃣ Align to candle boundaries
            from = align(from, intervalSec);
            to = align(to, intervalSec);

            // ---- 3️⃣ Resolve symbol + provider
            const [provider, symbol] = symbolInfo.full_name.split(':');

            if (!provider) {
                onHistory([], { noData: true });
                return;
            }

            // ---- 4️⃣ Validate saved chart state (optional gate)
            const chartStateRaw = localStorage.getItem(`chartState-${id}`);
            const savedChart = chartStateRaw ? JSON.parse(chartStateRaw) : undefined;

            const isChartResolutionMatch =
                !savedChart || savedChart?.charts?.[0]?.panes?.[0]?.sources?.[0]?.state?.interval === resolution;

            if (!isChartResolutionMatch) {
                console.log('Resolution mismatch, aborting fetch:', {
                    saved: savedChart?.charts?.[0]?.panes?.[0]?.sources?.[0]?.state?.interval,
                    current: resolution
                });
                onHistory([], { noData: true });
                return;
            }

            // ---- 5️⃣ Route fetch based on TradingView intent

            fetchHistory(
                id,
                symbol,
                intervalSec,
                { ...periodParams, from, to, firstDataRequest },
                provider,
                onHistory,
                onError
            );
        },
        searchSymbols: (userInput: string, exchange: string, symbolType: string, onResult: SearchSymbolsCallback) => {
            const state: RootState = store.getState();
            const pairs = state.marketPair.pairs;

            const temp = userInput.replace('/', '').toUpperCase();
            const filtered: any = pairs
                .filter((pair) => pair.show.toUpperCase().replace('/', '').includes(temp))
                .map((pair) => pair.show);
            // const filtered = allSymbols.filter((symbol) => symbol.match(temp));
            const returned = filtered.map((symbol) => ({
                description: symbol,
                exchange: '',
                full_name: symbol,
                symbol: symbol,
                ticker: symbol,
                type: getUniversalInstrument(symbol)?.type || ''
            }));
            onResult(returned);
        },
        subscribeBars: async (
            symbolInfo: LibrarySymbolInfo,
            resolution: ResolutionString,
            onTick: SubscribeBarsCallback,
            listenerGuid: string,
            onResetCacheNeededCallback: () => void
        ) => {
            if (intervalRef.current) clearInterval(intervalRef.current);

            const interval = setInterval(async () => {
                const state: RootState = store.getState();
                const updateBar = state.chartData.updateBar;
                if (
                    updateBar &&
                    updateBar?.symbol === symbolInfo.name &&
                    updateBar?.symbol === state.marketPair.activePair?.netdania
                ) {
                    const { symbol, ...bar } = updateBar;
                    onTick(bar);
                }
            }, 1000);

            intervalRef.current = interval;
        },
        unsubscribeBars: (listenerGuid: string) => {}
    } as IDatafeedChartApi & IExternalDatafeed;
};

function align(timeSec: number, resolutionSec: number): number {
    return Math.floor(timeSec / resolutionSec) * resolutionSec;
}
